import type { CalendarDay, Person, Summary } from './types';

export interface Delta {
  value: number;
  prev: number;
  pct: number | null;        // null => "new" (prev was 0, value > 0) or flat-zero
  arrow: 'up' | 'down' | 'flat' | 'new';
}

function delta(value: number, prev: number): Delta {
  if (prev === 0 && value === 0) return { value, prev, pct: null, arrow: 'flat' };
  if (prev === 0) return { value, prev, pct: null, arrow: 'new' };
  const pct = Math.round(((value - prev) / prev) * 100);
  return { value, prev, pct, arrow: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

function sum<T>(xs: T[], pick: (x: T) => number): number {
  let n = 0;
  for (const x of xs) n += pick(x);
  return n;
}

export interface HeroTile {
  label: string;
  value: number;
  delta: Delta;
}

export function heroTiles(days: CalendarDay[]): HeroTile[] {
  const last7 = days.slice(-7);
  const prev7 = days.slice(-14, -7);

  const activeReposIn = (ds: CalendarDay[]) => {
    const s = new Set<string>();
    for (const d of ds) for (const r of d.repo_activity) if (r.commits > 0) s.add(r.full_name);
    return s.size;
  };

  const commits = sum(last7, (d) => d.total_commits);
  const prs     = sum(last7, (d) => d.total_prs);
  const reviews = sum(last7, (d) => d.total_reviews);
  const repos   = activeReposIn(last7);

  return [
    { label: 'commits | 7d',       value: commits, delta: delta(commits, sum(prev7, (d) => d.total_commits)) },
    { label: 'PRs merged | 7d',    value: prs,     delta: delta(prs,     sum(prev7, (d) => d.total_prs)) },
    { label: 'reviews | 7d',       value: reviews, delta: delta(reviews, sum(prev7, (d) => d.total_reviews)) },
    { label: 'active repos | 7d',  value: repos,   delta: delta(repos,   activeReposIn(prev7)) },
  ];
}

// --- The Wall: per-person 90-day daily commit counts ---------------------

export interface WallCell { date: string; count: number; level: number }
export interface WallRow {
  canonical_id: string;
  display_name: string;
  primary_login: string | null;
  cells: WallCell[];
  total: number;
  lastActive: string;
}

function levelFor(n: number): number {
  if (n <= 0) return 0;
  if (n < 3)  return 1;
  if (n < 8)  return 2;
  if (n < 15) return 3;
  return 4;
}

export function wallRows(days: CalendarDay[], limit = 60): WallRow[] {
  const dates = days.map((d) => d.date);
  type Acc = { display_name: string; primary_login: string | null; byDate: Map<string, number>; lastActive: string };
  const perPerson = new Map<string, Acc>();

  for (const d of days) {
    for (const c of d.contributors) {
      if (c.commits <= 0) continue;
      let p = perPerson.get(c.canonical_id);
      if (!p) {
        p = { display_name: c.display_name, primary_login: c.primary_login, byDate: new Map(), lastActive: d.date };
        perPerson.set(c.canonical_id, p);
      }
      p.byDate.set(d.date, (p.byDate.get(d.date) ?? 0) + c.commits);
      if (d.date > p.lastActive) p.lastActive = d.date;
    }
  }

  const rows: WallRow[] = [];
  for (const [canonical_id, p] of perPerson) {
    const cells: WallCell[] = dates.map((date) => {
      const count = p.byDate.get(date) ?? 0;
      return { date, count, level: levelFor(count) };
    });
    let total = 0;
    for (const n of p.byDate.values()) total += n;
    rows.push({ canonical_id, display_name: p.display_name, primary_login: p.primary_login, cells, total, lastActive: p.lastActive });
  }
  rows.sort((a, b) => b.lastActive.localeCompare(a.lastActive));
  return rows.slice(0, limit);
}

// --- Cards -----------------------------------------------------------------

export interface CardRow {
  rank: number;
  canonical_id: string;
  display_name: string;
  primary_login: string | null;
  role_hint: string | null;
  value: number;
  valueSuffix?: string;
  spark: number[];
}

export interface CardDef {
  title: string;
  subtitle: string;
  rows: CardRow[];
}

function topEntries(map: Map<string, number>, limit = 5): [string, number][] {
  const arr = Array.from(map.entries()).filter(([, v]) => v > 0);
  arr.sort((a, b) => b[1] - a[1]);
  return arr.slice(0, limit);
}

function perPersonSum(days: CalendarDay[], field: 'commits' | 'prs' | 'reviews'): Map<string, number> {
  const m = new Map<string, number>();
  for (const d of days) {
    for (const c of d.contributors) {
      if (c[field] <= 0) continue;
      m.set(c.canonical_id, (m.get(c.canonical_id) ?? 0) + c[field]);
    }
  }
  return m;
}

function perPersonPRs(days: CalendarDay[], bhaiOnly = false): Map<string, number> {
  const m = new Map<string, number>();
  for (const d of days) {
    for (const pr of d.pr_events) {
      if (bhaiOnly && !pr.is_bhai_assisted) continue;
      m.set(pr.canonical_id, (m.get(pr.canonical_id) ?? 0) + 1);
    }
  }
  return m;
}

function perPersonDailySpark(days: CalendarDay[]): Map<string, number[]> {
  const m = new Map<string, number[]>();
  const len = days.length;
  for (let i = 0; i < len; i++) {
    for (const c of days[i].contributors) {
      if (c.commits <= 0) continue;
      let arr = m.get(c.canonical_id);
      if (!arr) { arr = new Array(len).fill(0); m.set(c.canonical_id, arr); }
      arr[i] += c.commits;
    }
  }
  return m;
}

function currentStreaks(days: CalendarDay[]): Map<string, number> {
  // For each person: count consecutive days back from the latest day with activity,
  // allowing the streak to end today OR yesterday (matches metrics.longest_current_streak).
  const len = days.length;
  if (len === 0) return new Map();
  const perDaily = perPersonDailySpark(days);
  const out = new Map<string, number>();
  for (const [cid, arr] of perDaily) {
    // Walk backwards from last index; allow skipping the very last day (grace).
    let i = len - 1;
    if (arr[i] === 0 && i - 1 >= 0 && arr[i - 1] > 0) i -= 1;
    let streak = 0;
    while (i >= 0 && arr[i] > 0) { streak++; i--; }
    if (streak > 0) out.set(cid, streak);
  }
  return out;
}

function reposTouched30(summary: Summary, org: string): Map<string, number> {
  // Derive from repos[org] top_contributors (30-day window). Count distinct repos per person.
  const repos = summary.repos[org] ?? [];
  const m = new Map<string, Set<string>>();
  for (const r of repos) {
    for (const tc of r.top_contributors) {
      let s = m.get(tc.canonical_id);
      if (!s) { s = new Set(); m.set(tc.canonical_id, s); }
      s.add(r.full_name);
    }
  }
  const out = new Map<string, number>();
  for (const [cid, s] of m) out.set(cid, s.size);
  return out;
}

export function buildCards(summary: Summary, org: string): CardDef[] {
  const days = summary.calendar[org]?.days ?? [];
  const last30 = days.slice(-30);
  const last7  = days.slice(-7);

  const spark30 = perPersonDailySpark(last30);
  const peopleByCid = new Map<string, Person>();
  for (const p of summary.people) peopleByCid.set(p.canonical_id, p);

  const mk = (
    entries: [string, number][],
    suffix?: string,
  ): CardRow[] =>
    entries.map(([cid, value], i) => {
      const p = peopleByCid.get(cid);
      return {
        rank: i + 1,
        canonical_id: cid,
        display_name: p?.display_name ?? cid,
        primary_login: p?.primary_login ?? null,
        role_hint: p?.role_hint ?? null,
        value,
        valueSuffix: suffix,
        spark: spark30.get(cid) ?? new Array(last30.length).fill(0),
      };
    });

  return [
    {
      title: 'Biggest pushers — last 7 days',
      subtitle: 'commits',
      rows: mk(topEntries(perPersonSum(last7, 'commits'))),
    },
    {
      title: 'Most repos touched — last 30 days',
      subtitle: 'breadth of work',
      rows: mk(topEntries(reposTouched30(summary, org))),
    },
    {
      title: 'PRs merged — last 30 days',
      subtitle: 'merged by each author',
      rows: mk(topEntries(perPersonPRs(last30))),
    },
    {
      title: 'Reviews given — last 30 days',
      subtitle: 'collaboration signal',
      rows: mk(topEntries(perPersonSum(last30, 'reviews'))),
    },
    {
      title: 'BHAI-driven PRs — last 30 days',
      subtitle: 'PRs flagged as BHAI-assisted',
      rows: mk(topEntries(perPersonPRs(last30, true))),
    },
    {
      title: 'Longest active streak',
      subtitle: 'consecutive days with a commit, ending today or yesterday',
      rows: mk(topEntries(currentStreaks(days)), 'd'),
    },
  ];
}

// --- Feed (last 48h) -------------------------------------------------------

export interface FeedEvent {
  canonical_id: string;
  display_name: string;
  primary_login: string | null;
  org: string;
  repo: string;
  number: number;
  title: string;
  is_bhai_assisted: boolean;
  bhai_ticket_ref: string | null;
  date: string;
}

export function feedEvents(days: CalendarDay[], limit = 20): FeedEvent[] {
  const last2 = days.slice(-2);
  const events: FeedEvent[] = [];
  for (const d of last2) {
    for (const pr of d.pr_events) {
      events.push({
        canonical_id:    pr.canonical_id,
        display_name:    pr.display_name,
        primary_login:   pr.primary_login,
        org:             pr.org,
        repo:            pr.repo,
        number:          pr.number,
        title:           pr.title,
        is_bhai_assisted: pr.is_bhai_assisted > 0,
        bhai_ticket_ref: pr.bhai_ticket_ref,
        date:            d.date,
      });
    }
  }
  events.sort((a, b) => b.date.localeCompare(a.date));
  return events.slice(0, limit);
}
